import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  getDiagnosticTest,
  submitDiagnosticAnswer,
  completeDiagnosticTest,
  saveDiagnosticQuestionnaire,
} from '../api/endpoints';
import {
  ChevronRight, CheckCircle, XCircle, Brain, Clock,
  AlertTriangle, X
} from 'lucide-react';
import Icon3D from '../components/Icon3D';

// ─── Pre-diagnostic questionnaire ───────────────────────────────────────────

const PDQScreen = ({ pdq, onComplete }) => {
  const [responses, setResponses] = useState({});

  if (!pdq) { onComplete({}); return null; }

  const items = pdq.items || [];
  const allAnswered = items.every(item => responses[item.id] !== undefined);

  return (
    <div className="min-h-screen bg-slate-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-xl w-full p-8">
        <div className="flex items-center gap-3 mb-2">
          <Icon3D icon={Brain} colour="purple" size="md" />
          <h2 className="text-xl font-bold text-slate-900">{pdq.title}</h2>
        </div>
        <p className="text-sm text-slate-500 mb-6">{pdq.subtitle}</p>

        <div className="space-y-6">
          {items.map((item) => (
            <div key={item.id}>
              <p className="font-medium text-slate-800 mb-3 text-sm">{item.text}</p>
              {item.format === 'scale_1_5' && (
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      onClick={() => setResponses(r => ({ ...r, [item.id]: val }))}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                        responses[item.id] === val
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                          : 'border-slate-200 text-slate-500 hover:border-cyan-300'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              )}
              {item.format === 'yes_no' && (
                <div className="flex gap-3">
                  {['Yes', 'No'].map(val => (
                    <button
                      key={val}
                      onClick={() => setResponses(r => ({ ...r, [item.id]: val }))}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                        responses[item.id] === val
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                          : 'border-slate-200 text-slate-500 hover:border-cyan-300'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              )}
              {item.scale_labels && (
                <div className="flex justify-between mt-1 text-xs text-slate-400">
                  <span>{item.scale_labels['1']}</span>
                  <span>{item.scale_labels['5']}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => allAnswered && onComplete(responses)}
          disabled={!allAnswered}
          className={`mt-8 w-full py-3 rounded-xl font-semibold text-white transition-all relative overflow-hidden ${
            allAnswered
              ? 'opacity-100 hover:opacity-90'
              : 'opacity-40 cursor-not-allowed'
          }`}
          style={{ background: allAnswered ? 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))' : '#94a3b8', ...(allAnswered ? { boxShadow: 'var(--shadow-3d-btn)' } : {}) }}
        >
          {allAnswered && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />}
          <span className="relative z-10">Begin Test 1</span>
        </button>
      </div>
    </div>
  );
};

// ─── Working memory terms intro ──────────────────────────────────────────────

const WorkingMemoryScreen = ({ terms, testNumber, onReady }) => {
  const [ready, setReady] = useState(false);

  return (
    <div className="min-h-screen bg-slate-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        <div className="flex justify-center mb-4">
          <Icon3D icon={Brain} colour="purple" size="lg" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Working Memory Task</h2>
        <p className="text-sm text-slate-500 mb-6">
          Please memorise these 4 terms. You will be asked to recall them later in the test.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {terms.map((term, i) => (
            <div
              key={i}
              className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 text-sm font-medium text-cyan-800 capitalize"
            >
              {term}
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 justify-center cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={ready}
            onChange={e => setReady(e.target.checked)}
            className="w-4 h-4 accent-cyan-600"
          />
          <span className="text-sm text-slate-600">I have memorised these terms</span>
        </label>
        <button
          onClick={() => ready && onReady()}
          disabled={!ready}
          className={`w-full py-3 rounded-xl font-semibold text-white transition-all relative overflow-hidden ${
            ready ? 'opacity-100 hover:opacity-90' : 'opacity-40 cursor-not-allowed'
          }`}
          style={{ background: ready ? 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))' : '#94a3b8', ...(ready ? { boxShadow: 'var(--shadow-3d-btn)' } : {}) }}
        >
          {ready && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />}
          <span className="relative z-10">Begin Questions</span>
        </button>
      </div>
    </div>
  );
};

// ─── Delayed recall screen (at start of Section D) ───────────────────────────

const DelayedRecallScreen = ({ terms, onComplete }) => {
  const [inputs, setInputs] = useState(['', '', '', '']);

  const handleSubmit = () => {
    const recalled = inputs.map(v => v.trim().toLowerCase());
    const correct = terms.map(t => t.toLowerCase());
    const score = recalled.filter(r => correct.some(c => c.includes(r) || r.includes(c))).length;
    onComplete({ recalled_terms: inputs, score_out_of_4: score });
  };

  return (
    <div className="min-h-screen bg-slate-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8">
        <div className="flex justify-center mb-4">
          <Icon3D icon={Brain} colour="purple" size="lg" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Recall Check</h2>
        <p className="text-sm text-slate-500 text-center mb-6">
          From the beginning of this test — can you recall the 4 working memory terms?
        </p>
        <div className="space-y-3 mb-6">
          {[0, 1, 2, 3].map(i => (
            <input
              key={i}
              type="text"
              placeholder={`Term ${i + 1}`}
              value={inputs[i]}
              onChange={e => {
                const v = [...inputs];
                v[i] = e.target.value;
                setInputs(v);
              }}
              className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none"
            />
          ))}
        </div>
        <button
          onClick={handleSubmit}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))', boxShadow: 'var(--shadow-3d-btn)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
          <span className="relative z-10">Continue to Final Section</span>
        </button>
      </div>
    </div>
  );
};

// ─── Main Test Runner ────────────────────────────────────────────────────────

const DiagnosticTestPage = () => {
  const { testId } = useParams();
  const testNumber = parseInt(testId, 10);
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading'); // loading | pdq | memory | questions | recall | done
  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workingMemoryTerms, setWorkingMemoryTerms] = useState([]);
  const [error, setError] = useState(null);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const questionStartTime = useRef(Date.now());

  useEffect(() => {
    document.title = `Diagnostic Test ${testNumber} | ${tenant?.name || 'MedMCQ'}`;
    loadTest();
  }, [testNumber]);

  // Warn before leaving mid-test
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (phase === 'questions') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [phase]);

  const loadTest = async () => {
    try {
      const res = await getDiagnosticTest(testNumber);
      const data = res.data;
      setTestData(data);
      const allQuestions = data.questions || [];
      setQuestions(allQuestions);
      setWorkingMemoryTerms(data.working_memory_terms || []);

      // Resume from where the user left off if they have prior answers
      const answeredIds = new Set(data.answered_question_ids || []);
      if (answeredIds.size > 0 && allQuestions.length > 0) {
        // Find the first unanswered question
        let resumeIndex = allQuestions.findIndex(q => !answeredIds.has(q.question_id));
        if (resumeIndex === -1) {
          // All questions answered — go to last question (will trigger completion)
          resumeIndex = allQuestions.length - 1;
        }
        setCurrentIndex(resumeIndex);
        // Skip PDQ and memory phases — go straight to questions
        setPhase('questions');
      } else {
        // Fresh start — show intro phases
        if (testNumber === 1 && data.pre_diagnostic_questionnaire) {
          setPhase('pdq');
        } else {
          setPhase('memory');
        }
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not load test. Please try again.';
      setError(msg);
      setPhase('error');
    }
  };

  const handlePDQComplete = async (responses) => {
    if (Object.keys(responses).length > 0) {
      try {
        await saveDiagnosticQuestionnaire({ responses });
      } catch (_) {}
    }
    setPhase('memory');
  };

  const handleMemoryReady = () => {
    setPhase('questions');
    questionStartTime.current = Date.now();
  };

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentSection = currentQuestion?.section;
  const needsConfidence = currentQuestion?.has_confidence_rating && !showFeedback;
  const showsImmediate = currentQuestion?.has_immediate_feedback;

  // Detect when entering Section D (delayed recall prompt)
  const prevSection = currentIndex > 0 ? questions[currentIndex - 1]?.section : null;
  const enteringRecall = currentSection === 'D' && prevSection !== 'D' && !showFeedback;

  const handleAnswerSelect = (index) => {
    if (showFeedback) return; // Only block changes AFTER submission, not before
    setSelectedAnswer(index);
  };

  const handleConfidenceSelect = (level) => {
    setConfidence(level);
  };

  const canSubmit = () => {
    if (selectedAnswer === null) return false;
    if (currentQuestion?.has_confidence_rating && confidence === null) return false;
    return true;
  };

  const handleSubmitAnswer = async () => {
    if (!canSubmit() || isSubmitting) return;
    setIsSubmitting(true);
    const responseTime = Date.now() - questionStartTime.current;

    const isCorrect = selectedAnswer === currentQuestion.correct_answer;

    try {
      await submitDiagnosticAnswer({
        question_id: currentQuestion.question_id,
        selected_answer: selectedAnswer,
        correct_answer: currentQuestion.correct_answer,
        test_number: testNumber,
        section: currentQuestion.section,
        confidence: confidence,
        response_time_ms: responseTime,
        is_correct: isCorrect,
        category: currentQuestion.category || null,
        sub_category: currentQuestion.sub_categories?.[0] || currentQuestion.sub_category || null,
        is_scenario: currentQuestion.is_scenario || false,
        has_explanation: Boolean(currentQuestion.explanation && currentQuestion.explanation.trim().length > 0),
      });
    } catch (_) {}

    if (showsImmediate) {
      setShowFeedback(true);
    } else {
      advanceQuestion();
    }

    setIsSubmitting(false);
  };

  const advanceQuestion = () => {
    if (isLastQuestion) {
      handleTestComplete();
      return;
    }
    const nextIndex = currentIndex + 1;
    const nextSection = questions[nextIndex]?.section;
    // Trigger delayed recall before Section D
    if (nextSection === 'D' && currentSection !== 'D') {
      setPhase('recall');
    } else {
      setCurrentIndex(nextIndex);
      setSelectedAnswer(null);
      setConfidence(null);
      setShowFeedback(false);
      questionStartTime.current = Date.now();
    }
  };

  const handleRecallComplete = (recallData) => {
    // Save recall data (best effort, don't block progress)
    submitDiagnosticAnswer({
      question_id: '__recall__',
      selected_answer: 0,
      correct_answer: 0,
      test_number: testNumber,
      section: 'recall',
      recall_data: recallData,
    }).catch(() => {});

    setPhase('questions');
    setCurrentIndex(questions.findIndex(q => q.section === 'D'));
    setSelectedAnswer(null);
    setConfidence(null);
    setShowFeedback(false);
    questionStartTime.current = Date.now();
  };

  const handleTestComplete = async () => {
    setPhase('done');
    try {
      await completeDiagnosticTest(testNumber);
    } catch (e) {
      console.error('Failed to mark test complete:', e);
    }
  };

  // ── Render phases ──────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-200 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4"><Icon3D icon={AlertTriangle} colour="orange" size="lg" /></div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Could not load test</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/diagnostic')}
            className="px-6 py-2 rounded-xl text-white font-semibold relative overflow-hidden hover:opacity-90 transition-all"
            style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
            <span className="relative z-10">Back to Diagnostic Hub</span>
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'pdq') {
    return (
      <PDQScreen
        pdq={testData?.pre_diagnostic_questionnaire}
        onComplete={handlePDQComplete}
      />
    );
  }

  if (phase === 'memory') {
    return (
      <WorkingMemoryScreen
        terms={workingMemoryTerms}
        testNumber={testNumber}
        onReady={handleMemoryReady}
      />
    );
  }

  if (phase === 'recall') {
    return (
      <DelayedRecallScreen
        terms={workingMemoryTerms}
        onComplete={handleRecallComplete}
      />
    );
  }

  if (phase === 'done') {
    const isLastTest = testNumber === 3;
    return (
      <div className="min-h-screen bg-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))' }}
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Test {testNumber} Complete!
          </h2>
          <p className="text-slate-500 mb-6">
            {isLastTest
              ? 'Outstanding! You\'ve completed all 3 diagnostic tests. Your personalised learning engine is now active — every question from here will be chosen specifically for you.'
              : `Great work! You've completed Test ${testNumber} of 3. Continue to Test ${testNumber + 1} when you're ready.`}
          </p>
          <button
            onClick={() => navigate(isLastTest ? '/diagnostic/results' : '/diagnostic')}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))', boxShadow: 'var(--shadow-3d-btn)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
            <span className="relative z-10">{isLastTest ? 'View Results & Dashboard' : 'Back to Diagnostic Hub'}</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Questions phase ────────────────────────────────────────────────────────

  if (!currentQuestion) return null;

  const progressPct = ((currentIndex + 1) / questions.length) * 100;
  const sectionLabel = { A: 'Section A', B: 'Section B', C: 'Section C', D: 'Final Section' };

  return (
    <div className="min-h-screen bg-slate-200">
      {/* Leave warning overlay */}
      {showLeaveWarning && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex justify-center mb-3"><Icon3D icon={AlertTriangle} colour="orange" size="md" /></div>
            <h3 className="text-lg font-bold text-slate-900 text-center mb-2">Leave the test?</h3>
            <p className="text-sm text-slate-500 text-center mb-6">
              Your progress within this test will be lost. Each test must be completed in one sitting.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveWarning(false)}
                className="flex-1 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
              >
                Stay
              </button>
              <button
                onClick={() => navigate('/diagnostic')}
                className="flex-1 py-2 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header bar */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40" style={{ height: '60px' }}>
        <div className="h-full flex items-center justify-between px-4 max-w-3xl mx-auto">
          <button
            onClick={() => setShowLeaveWarning(true)}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex-1 mx-4">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>{sectionLabel[currentSection] || currentSection}</span>
              <span>{currentIndex + 1} / {questions.length}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, rgb(6,182,212), rgb(8,145,178))',
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            <span>Test {testNumber}</span>
          </div>
        </div>
      </div>

      {/* Question card */}
      <div className="max-w-3xl mx-auto px-4 py-8" style={{ paddingTop: '80px' }}>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-4">
          {/* Section badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
              {sectionLabel[currentSection] || currentSection}
            </span>
            {currentQuestion.is_scenario && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200">
                Clinical Scenario
              </span>
            )}
          </div>

          {/* Question text */}
          <p className="text-slate-900 font-medium leading-relaxed mb-6">
            {currentQuestion.question}
          </p>

          {/* Answer options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, idx) => {
              const letter = String.fromCharCode(65 + idx);
              const isSelected = selectedAnswer === idx;
              const isCorrect = idx === currentQuestion.correct_answer;
              const showResult = showFeedback;

              let optStyle = 'border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 cursor-pointer';
              let textStyle = 'text-slate-700';
              let icon = null;

              if (showResult) {
                if (isCorrect) {
                  optStyle = 'border-emerald-400 bg-emerald-50';
                  textStyle = 'text-emerald-800 font-medium';
                  icon = <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />;
                } else if (isSelected && !isCorrect) {
                  optStyle = 'border-red-400 bg-red-50';
                  textStyle = 'text-red-700';
                  icon = <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
                } else {
                  optStyle = 'border-slate-100 opacity-50';
                }
              } else if (isSelected) {
                optStyle = 'border-cyan-500 bg-cyan-50';
                textStyle = 'text-cyan-800 font-medium';
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswerSelect(idx)}
                  disabled={showFeedback}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${optStyle}`}
                >
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                    showResult && isCorrect ? 'border-emerald-500 bg-emerald-100 text-emerald-700' :
                    showResult && isSelected ? 'border-red-500 bg-red-100 text-red-700' :
                    isSelected ? 'border-cyan-500 bg-cyan-100 text-cyan-700' :
                    'border-slate-300 text-slate-500'
                  }`}>
                    {letter}
                  </span>
                  <span className={`text-sm leading-relaxed flex-1 ${textStyle}`}>{option}</span>
                  {icon}
                </button>
              );
            })}
          </div>

          {/* Immediate feedback explanation (Section C) */}
          {showFeedback && currentQuestion.explanation && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Explanation</p>
              <p className="text-sm text-slate-700 leading-relaxed">{currentQuestion.explanation}</p>
            </div>
          )}
        </div>

        {/* Confidence selector (Section A) */}
        {currentQuestion.has_confidence_rating && !showFeedback && selectedAnswer !== null && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">
              How confident were you in that answer?
            </p>
            <div className="flex gap-3">
              {['Low', 'Medium', 'High'].map((level) => (
                <button
                  key={level}
                  onClick={() => handleConfidenceSelect(level.toLowerCase())}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                    confidence === level.toLowerCase()
                      ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                      : 'border-slate-200 text-slate-500 hover:border-cyan-300'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submit / Next button */}
        <div className="flex justify-end">
          {!showFeedback ? (
            <button
              onClick={handleSubmitAnswer}
              disabled={!canSubmit() || isSubmitting}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all relative overflow-hidden ${
                canSubmit() && !isSubmitting ? 'hover:opacity-90' : 'opacity-40 cursor-not-allowed'
              }`}
              style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))', ...(canSubmit() && !isSubmitting ? { boxShadow: 'var(--shadow-3d-btn)' } : {}) }}
            >
              {canSubmit() && !isSubmitting && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />}
              <span className="relative z-10 flex items-center gap-2">
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Submit Answer
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </span>
            </button>
          ) : (
            <button
              onClick={advanceQuestion}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white hover:opacity-90 transition-all relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgb(6,182,212), rgb(8,145,178))', boxShadow: 'var(--shadow-3d-btn)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
              <span className="relative z-10 flex items-center gap-2">
                {isLastQuestion ? 'Finish Test' : 'Next Question'}
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticTestPage;
